import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';


/**
 * Returns a shallow copy of the current history state without the cart drawer marker.
 * This avoids wiping state owned by Shopify, apps, or other theme components.
 * @returns {Record<string, unknown> | null}
 */
function getBaseHistoryState() {
  const currentState = history.state;
  if (!currentState || typeof currentState !== 'object' || Array.isArray(currentState)) return null;

  const nextState = { ...currentState };
  delete nextState.cartDrawerOpen;
  return Object.keys(nextState).length > 0 ? nextState : null;
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number} */
  #pageScrollY = 0;

  /** @type {ScrollRestoration | null} */
  #previousScrollRestoration = null;

  /** @type {boolean} */
  #opening = false;

  /** @type {number} */
  #stickyStateFrame = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(getBaseHistoryState(), '', window.location.href);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    if (this.#stickyStateFrame) cancelAnimationFrame(this.#stickyStateFrame);
    this.#restoreScrollRestoration();
  }

  /**
   * Stores the exact page position before the drawer locks the body.
   * On mobile, the temporary history entry lets the browser Back button close the drawer.
   */
  #prepareOpenState() {
    this.#pageScrollY = window.scrollY;

    if (!isMobileBreakpoint()) return;

    if ('scrollRestoration' in history) {
      this.#previousScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
    }

    this.#historyAbortController?.abort();
    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, {
      signal: this.#historyAbortController.signal,
    });

    // Push before DialogComponent fixes the body in place. This ensures the
    // previous history entry keeps the real page scroll position instead of 0.
    if (!history.state?.cartDrawerOpen) {
      history.pushState({ ...(getBaseHistoryState() ?? {}), cartDrawerOpen: true }, '', window.location.href);
    }
  }

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();

    if (!isMobileBreakpoint()) {
      this.#restorePagePosition();
      return;
    }

    // A manual close (X button / backdrop) must remove the temporary drawer
    // history entry. Restore the page only after that traversal completes so
    // Chrome cannot apply a second automatic scroll restoration afterward.
    if (history.state?.cartDrawerOpen) {
      let restored = false;
      const restoreAfterBack = () => {
        if (restored) return;
        restored = true;
        this.#restorePagePosition();
      };

      window.addEventListener('popstate', restoreAfterBack, { once: true });
      history.back();

      // Fallback for embedded previews where popstate can be delayed/suppressed.
      window.setTimeout(restoreAfterBack, 350);
      return;
    }

    // The browser Back button already removed the temporary entry.
    this.#restorePagePosition();
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #restorePagePosition() {
    const targetY = this.#pageScrollY;

    // Dialog close, focus restoration, and history traversal can each run in a
    // separate frame. Restore twice to keep the product page exactly in place.
    requestAnimationFrame(() => {
      window.scrollTo(0, targetY);
      requestAnimationFrame(() => {
        window.scrollTo(0, targetY);
        this.#restoreScrollRestoration();
      });
    });
  }

  #restoreScrollRestoration() {
    if (this.#previousScrollRestoration === null || !('scrollRestoration' in history)) return;
    history.scrollRestoration = this.#previousScrollRestoration;
    this.#previousScrollRestoration = null;
  }

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (event.detail?.data?.didError) return;
    if (this.hasAttribute('auto-open')) {
      this.open();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    if (this.refs.dialog?.open || this.#opening) return;

    this.#opening = true;
    this.#prepareOpenState();
    this.showDialog();

    // DialogComponent opens on the next animation frame.
    requestAnimationFrame(() => {
      this.#opening = false;
    });

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    if (this.#stickyStateFrame) cancelAnimationFrame(this.#stickyStateFrame);

    // Let showModal() and the drawer's first painted frame finish before measuring.
    // This avoids forcing layout in the same frame as the opening animation starts.
    this.#stickyStateFrame = requestAnimationFrame(() => {
      this.#stickyStateFrame = 0;

      const content = dialog.querySelector('.cart-drawer__content');
      const summary = dialog.querySelector('.cart-drawer__summary');

      if (!content || !summary) {
        dialog.setAttribute('cart-summary-sticky', 'false');
        return;
      }

      const drawerHeight = dialog.clientHeight;
      const summaryHeight = summary.offsetHeight;
      if (!drawerHeight) return;

      const ratio = summaryHeight / drawerHeight;
      dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
    });
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
